import ResultMapper from "./ResultMapper";
import { Fragment } from "react/jsx-runtime";
import { CalDataLine } from "./InnerCalendar";
import { CalendarContext } from "@/components/shared/VerticalCalendar";
import { useCallback, useContext, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ApiService from "@/api/ApiService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import ResInner from "./ResInner";
import CalendarJobBox from "./CalendarJobBox";
import CrewParts from "./CrewParts";
import { ChatBubble, CollectionsBookmarkOutlined, Edit, PeopleAlt, Weekend } from "@mui/icons-material";
import { textColors, colorsSortedNoBlack as allColors } from "@/shared/forms/ColorPicker";
import { useJobKeys } from "./useJobKeys";
import { DirectBookingCreator } from "../dialogs/manualBooking/DirectBookingCreator";
import { DirectBookingEditor } from "../dialogs/manualBooking/DirectBookingEditor";
import { DateRangePickerDay } from "@mui/lab";
import { useDeviceInfoModal } from "@/components/device/DeviceInfoModal";
import { useHolidayModal } from "../dialogs/HolidayModal";
import { Snackbar } from "@mui/material";
import { readPartDragData, resolveCalendarTarget } from "./calendarTarget";

export const nosec = (str) => (str.indexOf("Sekund") > -1 ? "heute" : str);

interface CalStaffMember {
  id: number;
  name: string;
  number: string;
}
interface CalCrew {
  id: number;
  name: string;
}
interface CalResource {
  id: number;
  name: string;
  number: string;
}
interface CalResourceOrder {
  id: number;
  comment: string;
  singleDayOrder: boolean;
}

interface CalResourceBooking {
  id: number;
  crew: number;
}

const today = dayjs().startOf("day");


const calculateAddMap = (date: string, add: number, jobs: string[], constraint: true | string[]) => {
  const newAddMap: { [date: string]: string } = {};
  if (!Array.isArray(jobs) || (constraint !== true && !Array.isArray(constraint))) return newAddMap;
  if (add !== 0) {
    const dates = add > 0 ? jobs : jobs.slice().reverse();
    newAddMap[date] = dayjs(date, "YYYY-MM-DD").add(add, "days").format("YYYY-MM-DD");
    let lastDate = newAddMap[date];
    for (const c of dates) {
      if ((add > 0 && c <= date) || (add < 0 && c >= date)) {
        continue;
      }
      if ((add > 0 && lastDate < c) || (add < 0 && lastDate > c)) {
        break;
      }
      let newDate = dayjs(lastDate, "YYYY-MM-DD").add(add > 0 ? 1 : -1, "day");
      while (newDate.isoWeekday() > 5 && !dates.includes(newDate.format("YYYY-MM-DD"))) {
        newDate = newDate.add(add > 0 ? 1 : -1, "day");
      }
      newAddMap[c] = newDate.format("YYYY-MM-DD");

      lastDate = newAddMap[c];
    }
  }
  if (constraint !== true) {
    const ccc = Object.fromEntries(Object.entries(newAddMap).map(([from, to]) => [from, !constraint.includes(to) ? "DELETE" : to]));
    return ccc;
  }
  return newAddMap;
};

interface ChangeData {
  move?: { phase: number; moveMap: { [origin: string]: string } } | null;
  resourceMove?: {
    phase: number;
    resource: number;
    order: number | null;
    delete: string[];
    create: string[];
  };
  orderToBooking?: {
    order: number;
    jobId: number | null;
    startDate: string;
    target: number;
  } | null;
  bookingMove?: {
    booking: number | null;
    startDate: string;
    order: number | null;
    target: number;
  } | null;
  partMove?: {
    resourceBookingId: number;
    jobId: number;
  } | null;
}

const blankImage = new Image();
blankImage.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";

const preventDefault = (e) => e.preventDefault();

export default function ResourceLine({
  offset,
  data,
  partDateSelectorRef,
  readOnly,
  hoverLine,
  setJobInfoId,
  setPositionEditorId,
  setHoverLine,
  settings,
  moveMap,
  setMoveMap,
  moveData,
  setMoveData,
  type,
}: {
  offset: number;
  readOnly: boolean;
  type: "CREWS" | "STAFF" | "RESOURCES";
  data: CalDataLine<CalStaffMember | CalCrew | CalResource, { resourceOrder: CalResourceOrder } | { resourceBooking: CalResourceBooking }>;
}) {
  const [startPageXY, setStartPageXY] = useState<[number, number] | null>(null);
  const [interactionError, setInteractionError] = useState<string | null>(null);
  const jobDragActive = useRef(false);
  const { fetchJobKeys } = useJobKeys();
  const { showByNumber } = useDeviceInfoModal();
  const { show } = useHolidayModal();
  const view = useContext(CalendarContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const changeMutation = useMutation<any, AxiosResponse, ChangeData>({
    mutationFn: ApiService.changeCalendar(),
    onSettled: (answer) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] === "getOpenRequests") {
            console.log("Invalidating query:", query.queryKey);
            return true;
          }

          if (query.queryKey[0] === "jobKeys") {
            console.log("Invalidating query:", query.queryKey);
            return true;
          }

          if (query.queryKey[0] === "getCalendar" && query.queryKey[3] >= answer.minWeek && query.queryKey[3] <= answer.maxWeek) {
            console.log("Invalidating query:", query.queryKey);
            return true;
          }

          return false;
        },
      });
      setMoveData(null);
      setMoveMap(null);
      setHoverLine(null);
      setStartPageXY(null);
    },
  });

  const deleteMutation = {
    mutate: (data) => changeMutation.mutate({ deleteBooking: { ...data, type } }),
  };

  const dragstart = useCallback(
    (e, elements, i) => {
      // Crew members have their own draggable descendants and data format.
      if (e.target !== e.currentTarget && e.target.closest?.('[draggable="true"]') !== e.currentTarget) return;
      jobDragActive.current = false;
      const target = resolveCalendarTarget(e, elements, i, view.zoom);
      if (!target) {
        e.preventDefault();
        setStartPageXY(null);
        setMoveData(null);
        setMoveMap(null);
        setInteractionError("Für diesen Tag ist kein planbarer Eintrag vorhanden.");
        return;
      }
      const { date: startDate, element } = target;
      jobDragActive.current = true;
      setInteractionError(null);
      e.dataTransfer.setDragImage(blankImage, 0, 0);
      e.dataTransfer.effectAllowed = "move";
      e.currentTarget.classList.add("dragging");
      //for (let el of document.querySelectorAll(".vc-sidebar.calendar")) el.style.height = window.getComputedStyle(el).height;
      fetchJobKeys(element.phase.id);
      if (data.id !== 0 && type === "RESOURCES" && settings["moveOnlyResource"]) {
        fetchJobKeys(element.phase.id, data.id);
      }
      setHoverLine({ id: data.id, offset, moveOnlyOrigin: e.shiftKey });
      setMoveData({
        ...element,
        origin: data.id,
        moveOnlyResource: data.id !== 0 && type === "RESOURCES" && settings["moveOnlyResource"],
        startDate,
      });
      setStartPageXY([e.pageX, e.pageY]);
    },
    [view.zoom, offset, data.id, type, settings["moveOnlyResource"], fetchJobKeys, setHoverLine, setMoveData, setMoveMap],
  );

  const dropPart = useCallback(
    (e, elements, i) => {
      e.preventDefault();
      e.currentTarget.classList.remove("dragover");
      const target = resolveCalendarTarget(e, elements, i, view.zoom);
      const dragged = readPartDragData(e.dataTransfer);
      if (!target || !dragged) {
        setInteractionError("Der Eintrag ist für diesen Tag nicht verfügbar. Bitte Kalender neu laden.");
        return;
      }
      setInteractionError(null);

      partDateSelectorRef.current?.openModal({
        ...dragged,
        startDate: target.date,
        jobId: target.element.id,
        crewId: data.id,
      });

    },
    [view.zoom, data.id, partDateSelectorRef],
  );

  const mouseMove = useCallback(
    (e) => {
      if (e.pageY === 0 || e.pageX === 0 || !startPageXY) return;
      const factor = view.zoom < 15 ? 7 : 1;
      const add = Math.round((e.pageX - startPageXY[0]) / view.zoom / factor) * factor;
      const constraintKeys = moveData.moveOnlyResource ? fetchJobKeys(moveData.phase.id) : true;
      const jobKeys = fetchJobKeys(moveData.phase.id, type === "RESOURCES" && settings["moveOnlyResource"] ? data.id : undefined);
      const addMap = !!jobKeys ? calculateAddMap(moveData.startDate, add, jobKeys, constraintKeys) : {};

      setMoveMap(addMap);
    },
    [view.zoom, startPageXY, moveData, settings["moveOnlyResource"]],
  );

  const mouseUp = useCallback(
    (e) => {
      if (e.pageY === 0 || e.pageX === 0 || !startPageXY) return;
      const factor = view.zoom < 15 ? 7 : 1;
      const add = Math.round((e.pageX - startPageXY[0]) / view.zoom / factor) * factor;
      const constraintKeys = moveData.moveOnlyResource ? fetchJobKeys(moveData.phase.id) : true;
      const jobKeys = fetchJobKeys(moveData.phase.id, type === "RESOURCES" && settings["moveOnlyResource"] ? data.id : undefined);
      const addMap = !!jobKeys ? calculateAddMap(moveData.startDate, add, jobKeys, constraintKeys) : {};
      if (!!moveData.moveOnlyResource) {
        const keys = new Set(Object.keys(addMap));
        const values = new Set(Object.values(addMap));

        const deleteSet = keys.difference(values);
        const createSet = values.difference(keys);

        changeMutation.mutate({
          resourceMove: {
            phase: moveData.phase.id,
            resource: moveData.origin,
            delete: Array.from(deleteSet),
            create: Array.from(createSet).filter((x) => x !== "DELETE"),
            order: !!moveData.resourceBooking.resourceOrder ? moveData.resourceBooking.resourceOrder?.id : null,
          },
        });
      } else if (!!moveData.resourceOrder) {
        changeMutation.mutate({
          move: { phase: moveData.phase.id, moveMap: addMap },
          orderToBooking:
            hoverLine.id === 0
              ? null
              : {
                  order: moveData.resourceOrder.id,
                  startDate: moveData.startDate,
                  jobId: hoverLine.moveOnlyOrigin || moveData.resourceOrder.singleDayOrder ? moveData.id : null,
                  target: hoverLine.id,
                },
          bookingMove: null,
        });
      } else {
        changeMutation.mutate({
          move: { phase: moveData.phase.id, moveMap: addMap },
          orderToBooking: null,
          /* hoverLine.id === 0
              ? null
              : {
                  order: moveData.resourceOrder.id,
                  jobId:
                    hoverLine.moveOnlyOrigin ||
                    moveData.resourceOrder.singleDayOrder
                      ? moveData.id
                      : null,
                  target: hoverLine.id,
                }, */
          bookingMove: {
            type,
            startDate: moveData.startDate,
            origin: moveData.origin,
            booking: hoverLine.moveOnlyOrigin || !moveData.resourceBooking.resourceOrder ? moveData.resourceBooking.id : null,
            order: !hoverLine.moveOnlyOrigin && !!moveData.resourceBooking.resourceOrder ? moveData.resourceBooking.resourceOrder?.id : null,
            target: hoverLine.id,
          },
        });
      }

      e.target.classList.remove("dragging");
    },
    [startPageXY, view.zoom, hoverLine, moveData, settings["moveOnlyResource"]],
  );

  const result = new ResultMapper();

  const days = [];
  const moving = [];

  for (let date in data.elements) {
    const exx = [];
    const dd = dayjs(date, "YYYY-MM-DD");

    if (dd < view?.calStart || dd > view?.calEnd) continue;

    for (let i = 0; i < data.elements[date].length; i++) {
      const element = data.elements[date][i];

      let changedOffset = offset;
      let changedDate = date;

      if (!!moveData && !!hoverLine && !element.vacation && element.type !== "manualBooking") {
        try {
          changedDate =
            (!moveData.moveOnlyResource || data.id === moveData.origin) && element.phase?.id === moveData.phase.id && !!moveMap
              ? (moveMap[date] ?? date)
              : date;
        } catch (e) {
          console.log(element, moveData);
          throw e;
        }

        if (!!moveData.resourceOrder !== !!element.resourceOrder || moveData.startDate > date) changedOffset = offset;
        else if (!!moveData.resourceOrder) {
          if (hoverLine.moveOnlyOrigin || moveData.resourceOrder.singleDayOrder) {
            changedOffset = moveData.id === element.id ? hoverLine.offset : offset;
          } else {
            changedOffset = moveData.resourceOrder.id === element.resourceOrder.id ? hoverLine.offset : offset;
          }
        } else if (hoverLine.moveOnlyOrigin) {
          changedOffset = moveData.resourceBooking.id === element.resourceBooking.id ? hoverLine.offset : offset;
        } else {
          changedOffset =
            moveData.resourceBooking.resourceOrder?.id === element.resourceBooking?.resourceOrder?.id && moveData.origin === data.id
              ? hoverLine.offset
              : offset;
        }
      }

      let hide = 0;
      if (changedDate === "DELETE") {
        hide = 2;
      } else if (changedDate !== date || changedOffset !== offset) {
        const xdd = dayjs(changedDate, "YYYY-MM-DD");
        hide = moveData.id !== element.id ? 1 : 2;

        if (xdd >= view?.calStart && xdd <= view?.calEnd) {
          const weekday = ((parseInt(xdd.format("d")) + 6) % 7) + 1;

          result.addMoving(xdd, element, changedOffset);

          moving.push(
            <div
              className={"vc-date-res moving" + (moveData.id === element.id ? " moveOrigin" : "") + " d" + weekday}
              key={"bgphasemove" + data.id + "_" + date + "_" + i}
              style={{
                gridRow: changedOffset,
                gridColumn: xdd.diff(view.calStart, "days") + 2,
              }}
            ></div>,
          );
        }
      }

      result.addNormal(dd, element, hide, view.zoom);
    }
  }

  if (!!settings.showAutobookings)
    result.addAutobookings(Object.values(data.autoBookings ?? {}), today > view?.calStart ? today : view?.calStart, view?.calEnd);

  const mergedList = result.getListBack(view?.zoom);
  const mergedMoving = result.getMovingListBack();

  for (let x of mergedList) {
    const date = x.start.format("YYYY-MM-DD");
    days.push(
      <div
        className={"vc-date-res boxWrapper" + (data.id === 0 ? " openline openline" + x.elements[date].length : "")}
        data-calendar-bar=""
        data-date={date}
        key={"phase" + data.id + "_" + date}
        style={{
          gridRow: offset,
          gridColumnStart: x.start.diff(view.calStart, "days") + 2,
          gridColumnEnd: x.end.diff(view.calStart, "days") + 3,
        }}
      >
        {x.elements[date].map(({ element, hide, type: xtype }, i) =>
          xtype === "normal" ? (
            <Fragment key={"phase" + data.id + "_" + date + "_" + i}>
              <CalendarJobBox
                projectId={element.phase.project.id}
                zoom={view.zoom}
                booked={!!element.resourceBooking}
                hide={hide}
                draggable={!readOnly}
                onDragOver={preventDefault}
                onDragEnter={type === "CREWS" && !!element.resourceBooking ? (e) => e.currentTarget.classList.add("dragover") : undefined}
                onDragLeave={type === "CREWS" && !!element.resourceBooking ? (e) => e.currentTarget.classList.remove("dragover") : undefined}
                onDrop={type === "CREWS" && !!element.resourceBooking ? (e) => dropPart(e, x.elements, i) : undefined}
                onDragStart={(e) => dragstart(e, x.elements, i)}
                onDrag={(e) => { if (jobDragActive.current && e.target === e.currentTarget) mouseMove(e); }}
                data-date={date}
                onDragEnd={(e) => {
                  if (e.target !== e.currentTarget) return;
                  e.currentTarget.classList.remove("dragging");
                  if (jobDragActive.current) {
                    jobDragActive.current = false;
                    mouseUp(e);
                  }
                }}
              >
                <ResInner
                  readOnly={readOnly}
                  did={data.id}
                  setJobInfoId={setJobInfoId}
                  elements={x.elements}
                  i={i}
                  changeMutation={changeMutation}
                  deleteMutation={deleteMutation}
                  type={type}
                />
              </CalendarJobBox>
              {type === "CREWS" && !!element.resourceBooking && hide === 0 ? (
                <CrewParts readOnly={readOnly} crewLineId={data.id} did={data.id} elements={x.elements} settings={settings} i={i} />
              ) : null}
            </Fragment>
          ) : xtype === "vacation" ? (
            <div
              onDragOver={preventDefault}
              className={"resourceOrder vacation" + (!!element.level ? " level" + element.level : "")}
              key={"vacation" + data.id + "_" + date + "_" + i}
            >
              {element.title}
            </div>
          ) : xtype === "manual" ? (
            <div onDragOver={preventDefault} className={"resourceOrder manual resourceBooking"} key={"manual" + data.id + "_" + date + "_" + i}>
              <div className="infocomp">
                <div className="name">{element.project.name}</div>
                {type !== "CREWS" && !!element.crew ? <div className="crewinfo">von Kolonne</div> : null}
                {!!element?.erfasser?.name ? (
                  <div className="info">
                    <PeopleAlt fontSize="inherit" />
                    <div className="txt">
                      {element?.erfasser?.name}, {element?.erfasser?.firstName}
                    </div>
                  </div>
                ) : null}
                {!!element?.comment ? (
                  <div className="info">
                    <ChatBubble fontSize="inherit" />
                    <div className="txt">{element.comment}</div>
                  </div>
                ) : null}
              </div>
              {!readOnly ? <DirectBookingEditor booking={element} /> : null}
            </div>
          ) : (
            <div
              style={
                view?.zoom < 40
                  ? {
                      backgroundColor: allColors[element.id % allColors.length],
                      color: textColors[allColors[element.id % allColors.length]],
                    }
                  : {}
              }
              onDragOver={preventDefault}
              className={"resourceOrder autobooking"}
              key={"autobooking" + data.id + "_" + date + "_" + i}
            >
              {element.name + " (" + element.number + ")"}
              <div className="resOrderEdit">
                <div className="resOrderButton">
                  <Edit onClick={() => navigate("/project/" + element.id, {})} fontSize="inherit" />
                </div>
              </div>
            </div>
          ),
        )}
      </div>,
    );
  }

  for (let x of mergedMoving) {
    const date = x.start.format("YYYY-MM-DD");
    const changedOffset = x.elements[date][0].changedOffset;
    const element = x.elements[date][0].element;
    let isOrigin = false;
    for (let xd in x.elements) {
      isOrigin = isOrigin || x.elements[xd][0].element.id === moveData.id;
    }

    moving.push(
      <div
        className={"vc-date-res moving" + (isOrigin ? " moveOrigin" : "") + " dnoBackground"}
        key={"phasemove" + data.id + "_" + element.id + "_" + date}
        style={{
          gridRow: changedOffset,
          gridColumnStart: x.start.diff(view.calStart, "days") + 2,
          gridColumnEnd: x.end.diff(view.calStart, "days") + 3,
        }}
      >
        <div className={"resourceOrder" + (!!element.resourceBooking ? " resourceBooking" : "")}>
          <ResInner elements={x.elements} setJobInfoId={setJobInfoId} did={data.id} i={0} changeMutation={false} deleteMutation={false} type={type} />
        </div>
      </div>,
    );
  }

  return (
    <Fragment>
      <Snackbar open={!!interactionError} message={interactionError} autoHideDuration={5000} onClose={() => setInteractionError(null)} />
      <div
        key={"sidebar" + offset}
        className={"vc-sidebar calendar" + (data.id === 0 ? " openline" : "")}
        style={{
          gridRow: offset,
        }}
      >
        {data.id === 0 ? (
          <em>offen</em>
        ) : (
          <Fragment>
            <div className={"vvss_name" + (type === "RESOURCES" || type === "STAFF" ? " dim_hover" : "")}>
              {type === "STAFF" ? (
                <div onClick={() => show(data.id)} className="dim_hover" style={{ whiteSpace: "nowrap" }}>
                  {data.name + (!!data?.number ? " (" + data.number + ")" : "")}
                </div>
              ) : (
                <div
                  onClick={type === "RESOURCES" ? () => showByNumber(data?.number) : undefined}
                  className={type === "RESOURCES" ? "dim_hover" : ""}
                >
                  {(!!data?.number ? data.number + ": " : "") + data.name}
                </div>
              )}
              {type === "RESOURCES" && !readOnly ? (
                <DirectBookingCreator staffMember={null} resource={data.id} crew={null} />
              ) : type === "STAFF" && !readOnly ? (
                <DirectBookingCreator staffMember={data.id} resource={null} crew={null} />
              ) : type === "CREWS" && !readOnly ? (
                <DirectBookingCreator crew={data.id} staffMember={null} resource={null} />
              ) : null}
            </div>

            {!!data.lastLocation ? (
              <div className="vvss_location">
                {!!data.lastLocation.free ? <div className="vvss_free">[frei]</div> : null}
                <div className="vvss_locname" title={!!data.lastLocation.project ? data.lastLocation.project.name : data.lastLocation.locationName}>
                  {!!data.lastLocation.project ? data.lastLocation.project.name : data.lastLocation.locationName}
                </div>
                <div className="vvss_age">({nosec(dayjs(data.lastLocation.date, "YYYY-MM-DD").from(dayjs().startOf("day")))})</div>
                {!readOnly ? (
                  <div className="vvss_edit" onClick={() => setPositionEditorId(data.id)}>
                    <Edit fontSize="inherit" />
                  </div>
                ) : null}
              </div>
            ) : null}
          </Fragment>
        )}
      </div>

      {days}
      {moving}
      <div
        className={"vc-hl" + (data.id === 0 ? " openline" : "")}
        onDragOver={(e) => !readOnly && !!moveData && !moveData.moveOnlyResource && setHoverLine({ id: data.id, offset, moveOnlyOrigin: e.shiftKey })}
        key={"hl" + offset}
        style={{
          pointerEvents: "initial",
          gridRow: offset,
          gridColumn: 2 + " / " + (view.calEnd.diff(view.calStart, "days") + 2),
        }}
      ></div>
    </Fragment>
  );
}
